import {
  CloseOutlined,
  EditOutlined,
  PauseCircleOutlined,
  PlayCircleFilled,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  AccordionItem,
  Alert,
  Button,
  ThreeDotMenu,
  Tooltip,
} from '@mm/components';
import { Overlay } from '@mm/components/src/Overlay/Overlay';
import { landmineNames } from '@mm/constants/landmine-names';
import { useResolutionInfo } from '@mm/hooks';
import { XRaySlideoutBanner } from '@mm/icons/SlideoutBanners';
import store from '@mm/state';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  chatWidgetTroubleshootingLastResultSelector,
  chatWidgetTroubleshootingNoIssuesMounted,
} from '@mm/state/selectors/chatWidgetTroubleshootingSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  activeChatbotIdSelector,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Empty, Switch } from 'antd';
import howItWorksSrc from 'assets/how-it-works.png';
import { ReactComponent as StarFigure } from 'assets/stars.svg';
import classNames from 'classnames';
import { orderBy } from 'lodash';
import { match } from 'path-to-regexp';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useRouter from 'use-react-router';
import { BookDemoButton } from '../../../../../../../../common/buttons/BookDemoButton/BookDemoButton';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import {
  Grid,
  GridCell,
  GridLabels,
  GridRow,
} from '../../../../../../../../common/components/Grid/Grid';
import { TableLabel } from '../../../../../../../../common/components/TableGrid/TableGrid';
import {
  useFreeTrialUpgradeModal,
  useShowTrialEndAlert,
} from '../../../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { useXRayBillingLimit } from '../../../../../../../../dashboard/pages/SettingsPage/BillingPanel/SubscriptionTabsArea/ActiveSubscriptionTab/BillingPageCard/BillingUserLimit/use-xray-billing-limit';
import { InstallationPageForm } from '../../../../../../../../dashboard/pages/XRayOnboarding/InstallationPage/InstallationPage';
import { XRAY_UTM } from '../../../../../../../../dashboard/pages/XRayOnboarding/WebsiteReportSummary/constants';
import { BotBuilderRoutes } from '../../../../../../../../routes/bot-builder-routes';
import { MOBILE_SMALL } from '../../../../../../../constants/screenSize';
import { DefaultCell } from '../../../../../../contacts/components/ContactsTable/ContactTable';
import { ReactComponent as XRayEmailIcon } from '../../../../Automations/AutomationEdit/InputTriggers/icons/x-ray.svg';
import SuccessExplainerImageSrc from './congratulations_explainer.jpg';
import css from './XRayIntegrations.module.css';

type Props = {
  className?: string;
};

type CellProps = { item: PromoterModel };

const renderDataCell = (field: string) => (props: CellProps) =>
  (
    <GridCell className={css.gridCell}>
      {(props?.item?.[field] ?? '-') as string}
    </GridCell>
  );
const renderEngagementRatioCell = ({ item }: CellProps) => (
  <GridCell className={css.gridCell}>
    {item.visitCountsLast24H?.total ? (
      <div className={css.engagementRatioCellContent}>
        {`${Math.max(
          0,
          Math.min(
            Math.round(
              (item.visitCountsLast24H.promoter /
                item.visitCountsLast24H.total) *
                100,
            ),
            100,
          ),
        )}%`}{' '}
        <span>
          <Tooltip
            anchorElement={<QuestionCircleOutlined />}
            color="dark"
            position="bottom"
            align="center"
            useHover
            withoutLine
          >
            This reflects the measure of how restrictive or flexible your
            trigger settings are (Last 24 hours)
          </Tooltip>
        </span>
      </div>
    ) : (
      '-'
    )}
  </GridCell>
);

const renderNameCell = (
  item: PromoterModel,
  onEditEntry: (item: PromoterModel) => void,
) => (
  <GridCell className="name-cell" onClick={() => onEditEntry(item)}>
    {item.name}
  </GridCell>
);

const StatusCell = ({
  item,
  toggleStatus,
}: CellProps & { toggleStatus: Function }) => {
  const freeTrialUpgradeModal = useFreeTrialUpgradeModal();
  const facebookPage = useSelector(activeFacebookPageSelector);

  const showTrialEndAlert = useShowTrialEndAlert(freeTrialUpgradeModal);

  return (
    <GridCell>
      {showTrialEndAlert ? (
        <Alert
          className={css.alert}
          css={{
            container: css.trialAlert,
          }}
          showCloseIcon={false}
          onClick={() =>
            freeTrialUpgradeModal.openFreeTrialUpgradeModal({
              landmineName: landmineNames.XRAY_DASHBOARD,
            })
          }
        >
          Upgrade your plan
        </Alert>
      ) : facebookPage?.xrayLockedAt ? (
        <span className={css.increaseLimit}>Increase limits</span>
      ) : (
        <div className={css.statusWrapper}>
          <Switch
            defaultChecked={item.active}
            onClick={() => toggleStatus(item)}
            size="small"
            className={css.switch}
          />

          <span
            className={classNames(css.tag, {
              [css.greenTag]: item.active,
            })}
          >
            {!item.active ? (
              <span className={css.tagContent}>
                <PauseCircleOutlined className={css.pauseIcon} /> Paused
              </span>
            ) : (
              <span className={css.tagContent}>
                <PlayCircleFilled className={css.playIcon} /> Capturing data
              </span>
            )}
          </span>
        </div>
      )}
    </GridCell>
  );
};

const renderStatusCell = ({
  item,
  toggleStatus,
}: CellProps & { toggleStatus: Function }) => (
  <StatusCell item={item} toggleStatus={toggleStatus} />
);

const AutomationCell = ({ item }: CellProps) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const history = useHistory();
  const automations = (item.usedInCampaigns || []).map((v, i) => (
    <Fragment key={v.id}>
      {!!i && <span>, </span>}
      <span
        key={v.id}
        className={css.link}
        onClick={() =>
          history.push(
            BotBuilderRoutes.automationEdit.getLink({ chatbotId, id: v.id }),
          )
        }
      >
        {v.name}
      </span>
    </Fragment>
  ));

  return (
    <GridCell className={classNames(css.automationLinks)} overflowable={false}>
      {automations.length ? automations : '-'}
    </GridCell>
  );
};

const AudienceCell = ({ item }: CellProps) => {
  const chatbotId = useSelector(activeChatbotIdSelector);

  const history = useHistory();

  const audiences = (item.audiencesNames || []).map(v => (
    <span
      key={v.id}
      className={css.link}
      onClick={() =>
        history.push(
          BotBuilderRoutes.audiences.getLink({ chatbotId, audienceId: v.id }),
        )
      }
    >
      {v.name}
    </span>
  ));

  return (
    <GridCell className={css.gridCell}>
      {audiences.length ? audiences : '-'}
    </GridCell>
  );
};

const renderActionsCell = (
  item: PromoterModel,
  onEditEntry: (id: PromoterModel) => void,
  onDeleteEntry: (item: PromoterModel) => void,
) => {
  const onSelectItem = (menuItem: string) => {
    switch (menuItem) {
      case 'delete':
        return onDeleteEntry(item);
      default:
        break;
    }
  };

  const state = store.getState();
  const isShopify = selectIsShopifyContext(state);

  const elements = [{ name: 'Delete', value: 'delete' }];

  const onMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <GridCell className="action-cell">
      <EditOutlined
        onClick={() => onEditEntry(item)}
        className="integrations__edit-icon"
      />
      {!isShopify && (
        <ThreeDotMenu
          className="integrations__three-dot-menu"
          elements={elements}
          selectItem={onSelectItem}
          onClick={onMenuClick}
        />
      )}
    </GridCell>
  );
};

const renderConditions = (item: PromoterModel) => {
  if (!item.conditions) {
    return 'All pages';
  }

  return item.conditions?.rules?.length
    ? `${
        item.conditions.ruleMode === 'any' ? 'Any:' : 'Every:'
      } ${item.conditions?.rules
        .map(v => `${v.operator} ${v.value}`)
        .join(', ')}`
    : '-';
};

const renderFromCell = (item: PromoterModel) => {
  const str = renderConditions(item);
  return (
    <div className={css.gridCellContainer}>
      <span className={css.tooltip}>{str}</span>
      <GridCell className={css.gridCell}>{str}</GridCell>
    </div>
  );
};

type OrderType = [key?: string, dir?: 'asc' | 'desc'];

const DEFAULT_ORDER = ['lastModified', 'desc'] as OrderType;

const generateTableCells = (
  onEditEntry: (item: PromoterModel) => void,
  onDeleteEntry: (item: PromoterModel) => void,
  onChangeOrder: (orderBy: string) => void,
  toggleStatus: (item: PromoterModel) => void,
  isShopify,
) => [
  {
    label: 'Name',
    keyName: 'name',
    rowClassName: 'name-cell',
    // onChange: onChangeOrder,
    Cell: (props: CellProps) => renderNameCell(props.item, onEditEntry),
  },
  {
    label: 'Status',
    keyName: 'active',
    // onChange: onChangeOrder,
    Cell: (props: CellProps) =>
      renderStatusCell({ item: props.item, toggleStatus }),
  },
  {
    label: 'Leads',
    // onChange: onChangeOrder,
    keyName: 'leads_captured',
    Cell: renderDataCell('sessionsStartedCount'),
  },
  {
    label: 'Impression Share',
    // onChange: onChangeOrder,
    keyName: 'engagement_ratio',
    Cell: renderEngagementRatioCell,
  },
  {
    label: 'Capture from',
    // onChange: onChangeOrder,
    keyName: 'from',
    Cell: ({ item }: CellProps) => renderFromCell(item),
  },
  {
    label: 'Audience',
    // onChange: onChangeOrder,
    keyName: 'audiences',
    Cell: (props: CellProps) => <AudienceCell {...props} />,
  },
  {
    label: isShopify ? '' : 'Automation',
    // onChange: onChangeOrder,
    keyName: 'automations',
    Cell: (props: CellProps) =>
      isShopify ? (
        <GridCell>
          <></>
        </GridCell>
      ) : (
        <AutomationCell {...props} />
      ),
  },
  {
    label: 'Actions',
    keyName: 'actions',
    Cell: ({ item }: CellProps) =>
      renderActionsCell(item, onEditEntry, onDeleteEntry),
  },
];

export const XRayGrid = ({
  className,
  botId,
  cap,
}: {
  className?: string;
  botId: number;
  cap?: number;
}) => {
  const { showSlideoutMenu } = useViewActions();
  const promoterCRUDActions = usePromoterCRUDActions();

  const modalActions = useModalActions();

  const promoters = useSelector((state: StoreState) =>
    (state.promoter.entities || [])
      .filter(p => p?.botId?.toString() === botId?.toString())
      .filter(p => p.type === 'getemails'),
  );
  const chatbotId = useSelector(activeChatbotIdSelector);
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_SMALL;

  const freeTrialUpgradeModal = useFreeTrialUpgradeModal();
  const showTrialEndAlert = useShowTrialEndAlert(freeTrialUpgradeModal);

  const gridTemplateColumns = '20% 15% 5% 10% 10% 15% 15% 10%';

  const onDeleteEntry = (entry: PromoterModel) => {
    const deleteCallback = async () => {
      promoterCRUDActions.delete(entry);
    };

    modalActions.openDeleteConfirmModal({
      headerText: `Are you sure you want to delete ${entry.name}?`,
      deleteCallback,
      newStyle: true,
    });
  };

  const toggleStatus = (entry: PromoterModel) => {
    const isActive = entry.active;

    const onCloseHandler = async (ok: boolean) => {
      if (ok) {
        promoterCRUDActions.update(chatbotId, {
          ...entry,
          active: !isActive,
        });
      }
    };

    modalActions.openGeneralConfirmModal({
      headerText: `Are you sure you want to ${
        isActive ? 'switch off' : 'switch on'
      } ${entry.name}?`,
      messageText: '',
      onCloseHandler,
      okButtonText: 'Yes',
      okButtonStyling: { blue: true },
      cancelButtonFirst: true,
      cancelButtonText: 'No',
    });
  };

  const [order, setOrder] = useState<OrderType>(DEFAULT_ORDER);

  const onChangeOrder = v => {
    const toSet = [v.keyName, v.orderDir] as OrderType;
    setOrder(toSet.join('') === order.join('') ? DEFAULT_ORDER : toSet);
  };

  const data = useMemo(() => {
    const [key, dir] = order;
    return orderBy(promoters, [v => v[key || '']?.toString()], dir);
  }, [promoters, order]);

  const onEditEntry = (promoter: PromoterModel) => {
    showSlideoutMenu('getemailsEditorNew', {
      promoterId: promoter.id,
    });
  };

  const isShopify = useSelector(selectIsShopifyContext);

  const cellData = generateTableCells(
    onEditEntry,
    onDeleteEntry,
    onChangeOrder,
    toggleStatus,
    isShopify,
  );

  return (
    <div className={classNames(className)}>
      {!data.length ? (
        <></>
      ) : (
        <Grid className="integrations-grid">
          {!!data.length && (
            <>
              <GridLabels
                className={classNames(
                  'integrations-grid__labels',
                  css.gridLabels,
                )}
                gridTemplateColumns={gridTemplateColumns}
              >
                {cellData.map(cell => (
                  <TableLabel
                    key={cell.keyName || cell.label}
                    dataItem={cell}
                    orderByAtTheEnd
                  />
                ))}
              </GridLabels>

              <div className="integrations-grid__rows-container">
                {data.slice(0, cap || data.length).map(item => (
                  <GridRow
                    key={item.id}
                    gridTemplateColumns={isMobile ? '1fr' : gridTemplateColumns}
                    className={css.gridRow}
                  >
                    {cellData.map(({ Cell, keyName }) =>
                      Cell ? (
                        <Cell key={keyName} item={item} />
                      ) : (
                        <DefaultCell
                          keyName={keyName as string}
                          item={{
                            ...item,
                            id: item.id || -1,
                          }}
                        />
                      ),
                    )}
                  </GridRow>
                ))}
              </div>
            </>
          )}
        </Grid>
      )}
    </div>
  );
};

export const XRayIntegrations = ({ className }: Props) => {
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const { retrieveAll, create } = usePromoterCRUDActions();

  const isShopify = useSelector(selectIsShopifyContext);

  const matchRoute = match(`${BotBuilderRoutes.integrations.path}/:route`)(
    location.pathname,
  ) as {
    params?: { chatbotId?: string };
  };
  const chatbotId = useSelector(activeChatbotIdSelector);
  const matchId = parseInt(matchRoute?.params?.chatbotId || '', 10);

  const { match: routerMatch } = useRouter();
  const isIntegrationsPage = routerMatch.path.includes(
    BotBuilderRoutes.integrations.path,
  );

  const botId = !isNaN(matchId)
    ? matchId
    : activeFacebookPage?.activeBotId || chatbotId;
  const [mounted, setMounted] = useState(false);
  const result = useSelector(chatWidgetTroubleshootingLastResultSelector);

  const [promotersReady, setPromotersReady] = useState(false);

  const [promoterId, setPromoterId] = useState<number | undefined>();

  const retrieveGetemailPromoters = async () => {
    try {
      if (botId) {
        const res = await retrieveAll(botId, 'getemails');
        if (res.length) {
          const firstPromoter = res?.[0];
          setPromoterId(firstPromoter.id);
        }
      }
    } finally {
      setPromotersReady(true);
    }
  };

  const [showSuccessModal, setShowSuccessModal] = useState<null | number>(null);

  const onClose = () => setShowSuccessModal(null);

  const history = useHistory();

  const [isNew, setIsNew] = useState(false);

  const navigateNewAutomation = async () => {
    const link = BotBuilderRoutes.automationNew.getLink({
      chatbotId: botId,
    });
    history.push(link);
  };

  const { showSlideoutMenu } = useViewActions();

  const onAddClick = async () => {
    const promoter = await create({
      botId,
      type: 'getemails',
      active: !promoterId,
    } as PromoterModel);

    if (promoter.id) {
      setPromoterId(promoter.id);
      showSlideoutMenu('getemailsEditorNew', {
        promoterId: promoter.id,
      });
    }
  };

  const onLimitUpdateSuccess = async () => {
    if (activeFacebookPage?.id) {
      connectedPageCRUDActions.retrievePage(activeFacebookPage?.id);
    }
  };

  const { updateXRayLimit } = useXRayBillingLimit({
    facebookPage: activeFacebookPage,
    subscription: activeFacebookPage?.subscription,
    onSuccess: onLimitUpdateSuccess,
    limitType: 'subscription',
  });

  useEffect(() => {
    retrieveGetemailPromoters();
  }, []);

  const connectedPageCRUDActions = useConnectedPageCRUDActions();

  useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }
  }, []);

  const noIssuesResultShown = useSelector(
    chatWidgetTroubleshootingNoIssuesMounted,
  );

  useEffect(() => {
    if (mounted && noIssuesResultShown) {
      setShowSuccessModal(1);
      setIsNew(false);
    }
  }, [noIssuesResultShown]);

  useEffect(() => {
    if (
      result?.onceSuccessfullyLoadedGlobalJs &&
      !activeFacebookPage?.onceSuccessfullyLoadedGlobalJs &&
      activeFacebookPage?.id
    ) {
      setShowSuccessModal(1);
      setIsNew(false);
      connectedPageCRUDActions.retrievePage(activeFacebookPage?.id);
    }
  }, [result?.onceSuccessfullyLoadedGlobalJs]);

  const hadSuccessChecks = useMemo(() => {
    return (
      result?.onceSuccessfullyLoadedGlobalJs ||
      activeFacebookPage?.onceSuccessfullyLoadedGlobalJs
    );
  }, [
    result?.onceSuccessfullyLoadedGlobalJs,
    activeFacebookPage?.onceSuccessfullyLoadedGlobalJs,
  ]);

  const showCongratulations = useMemo(() => {
    return hadSuccessChecks && !isNew;
  }, [hadSuccessChecks, !isNew]);

  const onProceedClick = () => {
    showSlideoutMenu('getEmailsEditorNewSettings', {
      promoterId,
    });
  };

  const howXRayWorksExpainer = (
    <div className={css.howItWorks}>
      <div className={css.howItWorksHeader}>How does the X-Ray Pixel work?</div>
      <div className={css.howItWorksContent}>
        <img src={howItWorksSrc} className={css.howItWorksImg} />
        <iframe
          width="495"
          height="277"
          src="https://player.vimeo.com/video/825855525?h=e882c0cd95&badge=0&autopause=0&player_id=0&app_id=58479"
          allowFullScreen
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          title="X-Ray Lead Generation Pixel Email Capture System in 90 Seconds"
          className={css.video}
        />
      </div>
    </div>
  );

  const emptyState = (
    <>
      <BookDemoButton
        className={classNames(css.bookDemoButton, css.bookDemoEmptyState, {
          [css.bookDemoIntegrations]: isIntegrationsPage,
        })}
        utm={XRAY_UTM}
      />
      <div className={css.empty}>
        <div className={css.emptyStateHeader}>
          Create and install your X-Ray Pixel to activate lead capture
          <Button
            leftIcon={<PlusOutlined />}
            onClick={onAddClick}
            css={{ container: css.emptyStateBtn }}
          >
            Create X-Ray Pixel
          </Button>
        </div>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          imageStyle={{ marginBlock: '0', height: 'auto' }}
          description=""
          className={css.emptyStateImage}
        />
      </div>
      {howXRayWorksExpainer}
    </>
  );

  return (
    <div
      className={classNames(className, css.container, 'integrations-component')}
    >
      {!!showSuccessModal && (
        <Overlay className={css.overlay}>
          <div className={css.modal}>
            <CloseOutlined className={css.closeIcon} onClick={onClose} />

            <span className={css.modalTitle}>
              <span className={css.stars}>
                <StarFigure />
              </span>
              Congratulations!
            </span>
            <span className={css.modalSubtitle}>
              Our secret code is now gathering leads from your website traffic.
              Lead contact information will appear on your dashboard and we will
              message you when it is ready
            </span>
            <div className={css.modalFigure}>
              <img src={SuccessExplainerImageSrc} alt="Explainer" />
            </div>

            <span>
              Use our tools to contact your leads with powerful outbounding
              automation
            </span>

            <Button onClick={onClose}>Close</Button>
          </div>
        </Overlay>
      )}

      {promotersReady && (
        <>
          {!!promoterId &&
            (hadSuccessChecks || activeFacebookPage?.getemailsActivated) && (
              <AccordionItem
                className={classNames('integrations-item', css.bannerContainer)}
                closeOnOutsideClick={false}
                caretStyleIcon
                openOnMount={false}
                title="Guide: How Customers.ai website visitor works"
              >
                <div className={css.bannerContent}>
                  <XRaySlideoutBanner textColor="normal" />
                </div>
              </AccordionItem>
            )}
          {hadSuccessChecks || activeFacebookPage?.getemailsActivated ? (
            promoterId ? (
              <>
                {activeFacebookPage?.xrayLockedAt &&
                  activeFacebookPage?.subscription?.xrayLimit && (
                    <Alert
                      type="warning"
                      showCloseIcon={false}
                      className={css.alert}
                    >
                      <div className={css.alertContainer}>
                        <div className={css.alertBody}>
                          <div className={css.alertHeader}>
                            X-Ray Pixel capture billing limit reached
                          </div>
                          To continue capturing leads, increase your monthly
                          capture limit
                        </div>
                        <PrimaryButton
                          onClick={updateXRayLimit}
                          className={css.alertButton}
                        >
                          Increase limits
                        </PrimaryButton>
                      </div>
                    </Alert>
                  )}
                <AccordionItem
                  className={classNames(
                    'integrations-item',
                    css.installationContainer,
                  )}
                  caretStyleIcon
                  canToggle={false}
                  closeOnOutsideClick={false}
                  openOnMount
                  titleComponent={
                    <div className={css.header}>
                      <XRayEmailIcon className={css.icon} />
                      <div>
                        <div className={css.title}>
                          X-Ray Lead Generation Pixel
                        </div>
                        <div className={css.subtitle}>
                          This allows us to gather leads from your traffic
                        </div>
                      </div>

                      {!isShopify && (
                        <div className={css.headerButtons}>
                          <BookDemoButton
                            className={css.bookDemoButton}
                            utm={XRAY_UTM}
                          />
                          <Button
                            css={{ container: css.add }}
                            color="blue"
                            leftIcon={<PlusOutlined size={18} />}
                            onClick={onAddClick}
                          >
                            Trigger
                          </Button>
                        </div>
                      )}
                    </div>
                  }
                >
                  <XRayGrid className={css.table} botId={botId} />
                </AccordionItem>
                <AccordionItem
                  className={classNames(
                    'integrations-item',
                    css.installVerifyContainer,
                  )}
                  closeOnOutsideClick={false}
                  caretStyleIcon
                  canToggle={true}
                  openOnMount={false}
                  title="Install or verify X-Ray Pixel"
                >
                  <div className={css.installation}>
                    <InstallationPageForm
                      botId={botId}
                      pageRemoteId={activeFacebookPage?.remoteId}
                      promoterId={promoterId}
                      canSkip={false}
                      canCloseResults
                      css={{
                        container: css.form,
                        title: css.formTitle,
                        diagnosticResultsContainer:
                          css.diagnosticResultsContainer,
                      }}
                      withVideo
                    />
                  </div>
                </AccordionItem>
              </>
            ) : (
              emptyState
            )
          ) : (
            <>
              {promotersReady && !promoterId && emptyState}

              {promotersReady && !!promoterId && (
                <>
                  <AccordionItem
                    className={classNames(
                      'integrations-item',
                      css.installationContainer,
                    )}
                    caretStyleIcon
                    canToggle={false}
                    closeOnOutsideClick={false}
                    openOnMount
                    titleComponent={
                      <div className={css.header}>
                        <XRayEmailIcon className={css.icon} />
                        <div>
                          <div className={css.title}>
                            X-Ray Lead Generation Pixel
                          </div>
                          <div className={css.subtitle}>
                            This allows us to gather leads from your traffic
                          </div>
                        </div>
                        <BookDemoButton
                          className={css.bookDemoButton}
                          utm={XRAY_UTM}
                        />
                      </div>
                    }
                  >
                    {showCongratulations && (
                      <div className={css.installation}>
                        <div className={css.onceInstalled}>
                          <h1>Congratulations!</h1>
                          <p>
                            Our secret code is now gathering leads from your
                            website traffic. Lead contact information will
                            appear on your dashboard and we will message you
                            when it is ready.
                          </p>
                          <p>
                            Use our tools to contact your leads with powerful
                            outbounding automation.
                          </p>
                          <Button
                            leftIcon={<PlusOutlined />}
                            onClick={navigateNewAutomation}
                          >
                            Automation
                          </Button>
                        </div>
                      </div>
                    )}
                    {!hadSuccessChecks && (
                      <InstallationPageForm
                        botId={botId}
                        pageRemoteId={activeFacebookPage?.remoteId}
                        promoterId={promoterId}
                        canSkip={false}
                        canCloseResults
                        css={{
                          container: classNames(css.form, css.coloredForm),
                          title: css.formTitle,
                          diagnosticResultsContainer:
                            css.diagnosticResultsContainer,
                          thirdStepButton: css.secondaryButton,
                        }}
                        thirdStepConfig={{
                          title: 'Proceed to settings',
                          button: 'Continue',
                          onClick: onProceedClick,
                        }}
                      />
                    )}
                  </AccordionItem>
                  {howXRayWorksExpainer}
                </>
              )}

              {!!showCongratulations && promotersReady && !!promoterId && (
                <AccordionItem
                  className={classNames(
                    'integrations-item',
                    css.installVerifyContainer,
                  )}
                  closeOnOutsideClick={false}
                  caretStyleIcon
                  openOnMount={false}
                  title="Install or verify X-Ray Pixel"
                >
                  <div className={css.installation}>
                    <InstallationPageForm
                      botId={botId}
                      pageRemoteId={activeFacebookPage?.remoteId}
                      promoterId={promoterId}
                      canSkip={false}
                      canCloseResults
                      css={{
                        container: css.form,
                        title: css.formTitle,
                        diagnosticResultsContainer:
                          css.diagnosticResultsContainer,
                      }}
                      thirdStepConfig={{
                        title: 'Proceed to settings',
                        button: 'Skip',
                        onClick: onProceedClick,
                      }}
                      withVideo
                    />
                  </div>
                </AccordionItem>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
